"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

// Global top loading bar (YouTube-style). Gives immediate feedback on every
// in-app navigation so a slow page doesn't look like an ignored click. It starts
// on internal link clicks / back-forward, trickles while the next route renders,
// and completes when the URL (path or query) actually changes.
//
// This complements per-route loading.tsx skeletons: the bar covers the brief gap
// before the destination commits (including cases a Suspense fallback can't show,
// e.g. an auth() call in a layout).
export function RouteProgress() {
  const pathname = usePathname();
  const search = useSearchParams();
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState(false);

  const trickle = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start the bar on same-origin link clicks and history navigation.
  useEffect(() => {
    function clearTrickle() {
      if (trickle.current) {
        clearInterval(trickle.current);
        trickle.current = null;
      }
    }
    function start() {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
      setActive(true);
      setWidth(8);
      clearTrickle();
      trickle.current = setInterval(() => {
        // Ease toward 90% and stop; the route change pushes it to 100%.
        setWidth((w) => (w < 90 ? w + Math.max(0.4, (90 - w) * 0.08) : w));
      }, 200);
      // If a click doesn't actually navigate, don't leave the bar hanging.
      safetyTimer.current = setTimeout(finish, 10_000);
    }
    function finish() {
      clearTrickle();
      setWidth(100);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => {
        setActive(false);
        setWidth(0);
      }, 250);
    }
    // expose start/finish via custom events so non-link navigations (e.g. the
    // admin org switcher's router.refresh) can drive the same bar
    document.addEventListener("routeprogress:start", start);
    document.addEventListener("routeprogress:finish", finish);

    function onClick(e: MouseEvent) {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      const target = anchor.getAttribute("target");
      if (
        !href ||
        href.startsWith("#") ||
        (target && target !== "_self") ||
        anchor.hasAttribute("download")
      ) {
        return;
      }
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Same URL → no navigation happens.
      const current = window.location.pathname + window.location.search;
      if (url.pathname + url.search === current) return;
      start();
    }
    function onPop() {
      start();
    }

    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("routeprogress:start", start);
      document.removeEventListener("routeprogress:finish", finish);
      window.removeEventListener("popstate", onPop);
      clearTrickle();
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
    };
  }, []);

  // Complete the bar when the route (path or query) commits.
  useEffect(() => {
    if (!active) return;
    document.dispatchEvent(new Event("routeprogress:finish"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  return (
    <div
      aria-hidden
      className={`pointer-events-none fixed inset-x-0 top-0 z-[9999] h-0.5 transition-opacity duration-200 ${
        active ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className="h-full bg-forest-500 shadow-[0_0_8px_var(--color-forest-500)] transition-[width] duration-200 ease-out"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
